import { Link } from 'react-router-dom'
import { useContactForm } from '@/hooks'
import { Input, Textarea, Select, Button } from '@/components/ui'
import { SEO } from '@/components/seo'

const timeOptions = [
  { value: '09:00', label: '09:00 AM' },
  { value: '09:30', label: '09:30 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '12:30', label: '12:30 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '13:30', label: '1:30 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '14:30', label: '2:30 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '15:30', label: '3:30 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '16:30', label: '4:30 PM' },
  { value: '17:00', label: '5:00 PM' },
]

export default function ContactPage() {
  const { formData, isSubmitting, isSubmitted, error, handleChange, handleSubmit } =
    useContactForm('demo')

  const today = new Date().toISOString().split('T')[0]

  return (
    <>
      <SEO
        title="Book a Call - WealthSpace | Schedule Your Demo"
        description="Book a personalised demo call with WealthSpace. Choose your preferred time and we'll show you how our platform can transform your financial advice practice."
        keywords="WealthSpace demo, book a call, schedule demo, financial adviser software demo, WealthSpace trial, book meeting"
        url="/contact"
      />

      {/* Hero */}
      <section className="py-[100px] px-6 relative z-[1]">
        <div className="max-w-[800px] mx-auto text-center">
          <h1 className="font-serif text-[clamp(42px,6vw,60px)] font-normal leading-[1.15] tracking-[-0.02em] mb-4">
            Let's <span className="text-accent-teal italic">talk</span>
          </h1>
          <p className="text-lg text-brand-muted leading-[1.7]">
            Book a personalised demo call with our team. We'll show you how WealthSpace can
            transform your practice.
          </p>
        </div>
      </section>

      {/* Two Column Layout */}
      <section className="px-6 pb-[100px] relative z-[1]">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Form */}
          <div>
            <h2 className="font-serif text-2xl font-normal text-brand-light mb-6">
              Book a <span className="text-accent-teal italic">call</span>
            </h2>
            <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl p-8">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-accent-teal/20 flex items-center justify-center mx-auto mb-6">
                    <svg
                      className="w-8 h-8 text-accent-teal"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="font-serif text-2xl text-brand-light mb-3">Thanks for booking!</h2>
                  <p className="text-brand-muted leading-relaxed">
                    We'll send you a calendar invite within 24 hours to confirm your preferred time,
                    or suggest an alternative if needed.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Info banner */}
                  <div className="bg-[rgba(20,184,166,0.08)] border border-accent-teal/20 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-accent-teal flex-shrink-0 mt-0.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      <span className="text-sm text-brand-muted leading-relaxed">
                        Pick your preferred date and time below. We'll send you a calendar invite to
                        confirm, or reach out if we need to adjust.
                      </span>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}

                  {/* Honeypot field - hidden from users */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input
                      label="Name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      label="Email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input
                      label="Company"
                      name="company"
                      type="text"
                      placeholder="Your company name"
                      value={formData.company}
                      onChange={handleChange}
                    />
                    <Input
                      label="Phone"
                      name="phone"
                      type="tel"
                      placeholder="Your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input
                      label="Preferred Date"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={today}
                      required
                    />
                    <Select
                      label="Preferred Time (UK)"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      options={timeOptions}
                      placeholder="Select a time..."
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <Textarea
                      label="Anything you'd like us to cover?"
                      name="message"
                      placeholder="Tell us about your practice, what you're looking for, or any specific features you'd like to see..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full justify-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Request Call
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </>
                    )}
                  </Button>

                  <p className="text-[13px] text-brand-subtle text-center mt-4">
                    Calls are typically 30 minutes via Google Meet or Zoom
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Right: Contact Information */}
          <div>
            <h2 className="font-serif text-2xl font-normal text-brand-light mb-6">
              Contact <span className="text-accent-teal italic">information</span>
            </h2>

            <div className="space-y-8">
              {/* Email */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(20,184,166,0.1)] border border-[rgba(20,184,166,0.2)] flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-accent-teal"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-semibold text-brand-subtle uppercase tracking-wider mb-2">
                    Email
                  </div>
                  <a
                    href="mailto:support@wealthspace.ai"
                    className="block text-accent-teal hover:text-accent-tealLight transition-colors"
                  >
                    support@wealthspace.ai
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(20,184,166,0.1)] border border-[rgba(20,184,166,0.2)] flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-accent-teal"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-semibold text-brand-subtle uppercase tracking-wider mb-2">
                    Phone
                  </div>
                  <a
                    href="tel:01225541226"
                    className="block text-accent-teal hover:text-accent-tealLight transition-colors"
                  >
                    01225 541226
                  </a>
                </div>
              </div>

              {/* Office */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(20,184,166,0.1)] border border-[rgba(20,184,166,0.2)] flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-accent-teal"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-semibold text-brand-subtle uppercase tracking-wider mb-2">
                    Office
                  </div>
                  <div className="text-brand-muted leading-relaxed">
                    <div className="text-brand-light font-medium">WealthSpace Ltd</div>
                    <div>2nd & 3rd Floors Northgate House</div>
                    <div>Upper Borough Walls, Bath, BA1 1RG</div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(20,184,166,0.1)] border border-[rgba(20,184,166,0.2)] flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-accent-teal"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-semibold text-brand-subtle uppercase tracking-wider mb-2">
                    Response Time
                  </div>
                  <div className="text-brand-muted leading-relaxed">
                    We typically respond within 24 hours during business days.
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-xl p-6 mt-8">
                <h3 className="font-semibold text-brand-light mb-4">Quick actions</h3>
                <div className="space-y-3">
                  <a
                    href="#contact-form"
                    className="flex items-center gap-3 text-brand-muted hover:text-accent-teal transition-colors group"
                  >
                    <svg
                      className="w-5 h-5 text-accent-teal"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span className="group-hover:translate-x-1 transition-transform">
                      Book a call
                    </span>
                  </a>
                  <Link
                    to="/get-started"
                    className="flex items-center gap-3 text-brand-muted hover:text-accent-teal transition-colors group"
                  >
                    <svg
                      className="w-5 h-5 text-accent-teal"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                    <span className="group-hover:translate-x-1 transition-transform">
                      Start free trial
                    </span>
                  </Link>
                  <Link
                    to="/partner-programme"
                    className="flex items-center gap-3 text-brand-muted hover:text-accent-teal transition-colors group"
                  >
                    <svg
                      className="w-5 h-5 text-accent-teal"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                    <span className="group-hover:translate-x-1 transition-transform">
                      Partner programme
                    </span>
                  </Link>
                </div>
              </div>

              {/* Social Links */}
              <div className="pt-4">
                <div className="text-xs font-semibold text-brand-subtle uppercase tracking-wider mb-3">
                  Follow us
                </div>
                <a
                  href="https://linkedin.com/company/wealthspace"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full bg-white/[0.03] border border-white/[0.1] flex items-center justify-center hover:bg-[rgba(20,184,166,0.15)] hover:border-[rgba(20,184,166,0.3)] hover:text-accent-teal hover:-translate-y-0.5 transition-all"
                >
                  <svg className="w-5 h-5 text-brand-muted" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 max-w-[900px] mx-auto text-center relative z-[1]">
        <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] rounded-3xl py-16 px-12">
          <h2 className="font-serif text-[32px] font-normal text-brand-light tracking-[-0.02em] mb-5">
            Ready to transform your <span className="text-accent-teal italic">practice</span>?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            Join the advisers who are already working smarter, not harder.
          </p>
          <Button href="/get-started" size="lg">
            Get Started
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      </section>
    </>
  )
}
